<template>
<div>
    <!-- KPI strip -->
    <div class="pl-kpis">
        <div class="pl-kpi">
            <div class="pl-kpi-badge pl-kpi-good">✓</div>
            <div><div class="pl-kpi-big">{{ doneCount }} <small>/ {{ daySlots.length }}</small></div>
            <div class="pl-kpi-lbl">processes done today</div></div>
        </div>
        <div class="pl-kpi">
            <div class="pl-kpi-badge pl-kpi-ink">◷</div>
            <div><div class="pl-kpi-big">{{ fmtSecs(totalPlanned) }} <small>/ {{ fmtSecs(totalCapacity) }}</small></div>
            <div class="pl-kpi-lbl">planned vs capacity</div></div>
        </div>
        <div class="pl-kpi">
            <div class="pl-kpi-badge pl-kpi-warn">↩</div>
            <div><div class="pl-kpi-big">{{ board.carryover.length }}</div>
            <div class="pl-kpi-lbl">unfinished from earlier days</div></div>
        </div>
        <div class="pl-kpi">
            <div class="pl-kpi-badge" :class="exceptionCount ? 'pl-kpi-crit' : 'pl-kpi-good'">!</div>
            <div><div class="pl-kpi-big">{{ exceptionCount }}</div>
            <div class="pl-kpi-lbl">exceptions to clear</div></div>
        </div>
    </div>

    <div class="pl-dayboard">
        <div class="pl-daycols">
            <div class="pl-daycol" v-for="person in board.people" :key="person.id"
                 :class="{ 'pl-day-dim': colDimmed(person), 'pl-daycol-target': hoverCol === String(person.id) }">
                <div class="pl-person">
                    <span class="pl-av">{{ initials(person.name) }}</span>
                    <div>
                        <div class="pl-person-nm">{{ person.name }}</div>
                        <span v-if="person.auto_fill_percent !== null" class="pl-day-lim">⚡ ≤ {{ person.auto_fill_percent }}%</span>
                    </div>
                </div>
                <div class="pl-cap pl-daycap" :class="bandClass(pct(person))" v-if="capacity(person) > 0">
                    <span class="pl-cap-track">
                        <span class="pl-cap-fill" :style="{ width: Math.min(pct(person), 100) + '%' }"></span>
                        <span v-if="person.auto_fill_percent !== null" class="pl-cap-lim" :style="{ left: Math.min(person.auto_fill_percent, 100) + '%' }"></span>
                    </span>
                    <span class="pl-cap-n">{{ pct(person) }}% <small>· {{ fmtSecs(planned(person)) }}/{{ fmtSecs(capacity(person)) }}</small></span>
                </div>
                <div class="pl-cap pl-daycap" v-else><span class="pl-cap-n pl-cap-none">no set hours</span></div>

                <draggable class="pl-daylist"
                           :list="listFor(person.id)"
                           group="dayslots"
                           :disabled="!canWrite || selecting"
                           :data-user-id="person.id"
                           v-bind="dragOpts"
                           :move="onMoveCheck"
                           @start="onDragStart($event, listFor(person.id))"
                           @end="onDragEnd"
                           @change="onColChange(person, $event)">
                    <div v-for="slot in listFor(person.id)" :key="slot.id"
                         class="pl-chip pl-daychip"
                         :class="{ 'pl-chip-done': slot.checked, 'pl-chip-ghost': !slot.released && !slot.checked, 'pl-chip-typed': slot.type !== 'process', 'pl-no-drag': !slotMovable(slot),
                                   'pl-chip-sel': !!selectedIds[slot.id], 'pl-chip-unsel': selecting && !slotMovable(slot) }"
                         :style="{ '--plwc': wc.colour(slot.workcentre_id, slot.type) }"
                         @click="$emit('pick', slot, $event)">
                        <span v-if="selecting && slotMovable(slot)" class="pl-chip-ck" aria-hidden="true">{{ selectedIds[slot.id] ? '✓' : '' }}</span>
                        <!-- P4 corner mark, same as the Week grid: ⏳ waiting on artwork / ★ priority -->
                        <span v-else-if="!selecting && chipMark(slot)" class="pl-chip-flag" :title="chipMarkTitle(slot)">{{ chipMark(slot) }}</span>
                        <button v-if="permissions.can_tick" type="button" class="pl-tick"
                                :class="{ 'pl-tick-on': slot.checked }"
                                :disabled="!!ticking[slot.id]"
                                :aria-label="slot.checked ? 'Undo tick' : 'Mark complete'"
                                @click.stop="quickTick(slot)">{{ slot.checked ? '✓' : '' }}</button>
                        <svg class="pl-i"><use :href="'#' + wc.icon(slot.workcentre_id, slot.type)"/></svg>
                        <div class="pl-chip-cx">
                            <div class="pl-chip-r1">
                                <span class="pl-chip-nm">{{ slot.checked ? '✓ ' : '' }}{{ slot.pretty_name }}</span>
                                <span class="pl-chip-mt">{{ fmtSecs(slot.planned_seconds) }}</span>
                            </div>
                            <div class="pl-chip-r2">
                                <span class="pl-chip-cq">{{ slot.quote_id ? ('Q' + slot.quote_id + ' · ' + (slot.quote_name || '')) : (slot.type === 'delivery' ? 'Delivery' : 'Ad-hoc') }}</span>
                                <span v-if="slot.is_draft" class="pl-pill pl-pill-draft">Draft</span>
                            </div>
                        </div>
                    </div>
                    <template slot="footer">
                        <div v-if="!listFor(person.id).length" class="pl-day-empty">
                            {{ canWrite ? 'nothing planned — drop work here' : 'nothing planned' }}
                        </div>
                    </template>
                </draggable>
            </div>
        </div>

        <!-- exceptions rail -->
        <aside class="pl-rail">
            <h2>⚠ Exceptions</h2>
            <div class="pl-rail-sub">Manage today by exception — on a good day this list is empty</div>

            <div class="pl-exc pl-exc-crit" v-for="p in offWithWork" :key="'off' + p.id">
                <div class="pl-exc-tx">
                    <b>Off today — {{ p.name }}</b>
                    <p>No hours set for today, but {{ fmtSecs(p.planned) }} of work is planned on them.</p>
                    <button v-if="permissions.can_replan" type="button" class="pl-exc-act" @click="reassignPersonDay(p)">
                        Reassign their day
                    </button>
                </div>
            </div>

            <div class="pl-exc pl-exc-warn" v-for="c in board.carryover" :key="'co' + c.slot_id">
                <div class="pl-exc-tx">
                    <b>Unfinished — {{ c.user_name }}</b>
                    <p>{{ c.pretty_name }}<template v-if="c.quote_id"> (Q{{ c.quote_id }} · {{ c.quote_name }})</template>
                    was planned for {{ c.date }} and isn't ticked.</p>
                    <button v-if="canWrite" type="button" class="pl-exc-act" @click="moveToToday(c)">Move to today</button>
                </div>
            </div>

            <div class="pl-exc pl-exc-warn" v-for="g in lateAddGroups" :key="'la' + g.quote_id">
                <div class="pl-exc-tx">
                    <b>Unscheduled late add</b>
                    <p>{{ g.count }} process{{ g.count > 1 ? 'es' : '' }} added to Q{{ g.quote_id }} ·
                    {{ g.quote_name }} after release — needs a person + day.</p>
                    <button v-if="canWrite" type="button" class="pl-exc-act" @click="$emit('assign-quote', g.quote_id)">Assign →</button>
                </div>
            </div>

            <div class="pl-exc pl-exc-ink" v-for="p in spareCapacity" :key="'sp' + p.id">
                <div class="pl-exc-tx">
                    <b>Spare capacity — {{ p.name }}</b>
                    <p>{{ fmtSecs(p.spare) }} of headroom left today.</p>
                </div>
            </div>

            <div class="pl-exc pl-exc-ok" v-if="!exceptionCount">
                <div class="pl-exc-tx"><b>All clear</b><p>Nothing needs attention.</p></div>
            </div>
        </aside>
    </div>
</div>
</template>

<script>
import draggable from 'vuedraggable';
import { fmtSecs, bandClass, adjustedPct, initials, errMsg, wcLookups, DRAG_OPTS } from './shared';

export default {
    name: 'DayView',
    components: { draggable },

    props: {
        board: { type: Object, required: true },
        permissions: { type: Object, required: true },
        // multi-select lives in PlannerPage; chips here just paint it and
        // report clicks upward (`pick` carries the MouseEvent for Ctrl-click)
        selecting: { type: Boolean, default: false },
        selectedIds: { type: Object, default: () => ({}) },
    },

    data() {
        return {
            ticking: {},
            lists: {},       // userId -> mutable slot array (vuedraggable owns these)
            dragging: null,  // the slot in hand — dims non-matching columns
            hoverCol: null,  // userId string of the column the drag would drop into
        };
    },

    created() {
        this.rebuildLists();
    },

    watch: {
        // The board object is replaced wholesale on every fetch.
        board() {
            this.rebuildLists();
        },
    },

    computed: {
        wc() {
            return wcLookups(this.board.workcentres);
        },
        dragOpts() {
            // tick circles stay clickable without lifting the chip
            return Object.assign({}, DRAG_OPTS, { filter: '.pl-tick, .pl-no-drag' });
        },
        date() {
            return this.board.dates[0];
        },
        daySlots() {
            return this.board.slots.filter(s => s.date === this.date);
        },
        doneCount() {
            return this.daySlots.filter(s => s.checked).length;
        },
        totalPlanned() {
            return this.daySlots.reduce((sum, s) => sum + (s.planned_seconds || 0), 0);
        },
        totalCapacity() {
            return this.board.people.reduce((sum, p) => sum + this.capacity(p), 0);
        },
        canWrite() {
            return this.permissions.can_place || this.permissions.can_replan;
        },
        lateAddGroups() {
            return this.board.tray
                .map(g => ({ quote_id: g.quote_id, quote_name: g.quote_name, count: g.processes.filter(p => p.late_add).length }))
                .filter(g => g.count > 0);
        },
        spareCapacity() {
            return this.board.people
                .map(p => ({ id: p.id, name: p.name, cap: this.capacity(p), spare: this.capacity(p) - this.planned(p) }))
                .filter(p => p.cap > 0 && p.spare >= 3600);
        },
        offWithWork() {
            return this.board.people
                .map(p => ({
                    id: p.id,
                    name: p.name,
                    cap: this.capacity(p),
                    planned: this.slotsFor(p).filter(s => !s.checked).reduce((sum, s) => sum + (s.planned_seconds || 0), 0),
                }))
                .filter(p => p.cap === 0 && p.planned > 0);
        },
        exceptionCount() {
            return this.board.carryover.length + this.lateAddGroups.length + this.offWithWork.length;
        },
    },

    methods: {
        fmtSecs,
        bandClass,
        initials,

        slotsFor(person) {
            return this.daySlots
                .filter(s => s.user_id === person.id)
                .sort((a, b) => a.pos - b.pos);
        },

        // ---------- P4 job flags (read off board.jobs by quote) ----------
        jobFlags(quoteId) {
            if (!quoteId) return null;
            const job = (this.board.jobs || []).find(j => j.quote_id === quoteId);
            return job ? (job.flags || null) : null;
        },
        /* Chip corner mark — the two flags a person at the bench acts on. */
        chipMark(slot) {
            const f = this.jobFlags(slot.quote_id);
            if (!f) return '';
            return (f.artwork === 'waiting' ? '⏳' : '') + (f.priority ? '★' : '');
        },
        chipMarkTitle(slot) {
            const f = this.jobFlags(slot.quote_id) || {};
            const parts = [];
            if (f.artwork === 'waiting') parts.push('Waiting on artwork' + (f.artwork_by ? ' (' + f.artwork_by + ')' : ''));
            if (f.priority) parts.push('Priority job');
            return parts.join(' · ');
        },

        // ---------- drag between people ----------
        rebuildLists() {
            const lists = {};
            this.board.people.forEach(p => { lists[p.id] = []; });
            this.daySlots.forEach(s => {
                if (!lists[s.user_id]) lists[s.user_id] = [];
                lists[s.user_id].push(s);
            });
            Object.keys(lists).forEach(k => lists[k].sort((a, b) => a.pos - b.pos));
            this.lists = lists;
        },
        listFor(userId) {
            if (!this.lists[userId]) this.$set(this.lists, userId, []);
            return this.lists[userId];
        },
        slotMovable(slot) {
            if (slot.checked) return false;
            return slot.released ? this.permissions.can_replan : this.permissions.can_place;
        },
        onDragStart(evt, list) {
            this.dragging = list[evt.oldIndex] || null;
            this.$emit('dragstate', true);
        },
        onDragEnd() {
            this.dragging = null;
            this.hoverCol = null;
            this.$emit('dragstate', false);
        },
        onMoveCheck(evt) {
            const ok = this.slotMovable(evt.draggedContext.element);
            const to = evt.to || {};
            this.hoverCol = (ok && to.dataset) ? (to.dataset.userId || null) : null;
            return ok;
        },
        colDimmed(person) {
            if (!this.dragging || !this.dragging.workcentre_id) return false;
            const ids = person.workcentre_ids || [];
            return ids.length > 0 && ids.indexOf(this.dragging.workcentre_id) === -1;
        },
        onColChange(person, evt) {
            const info = evt.added || evt.moved;
            if (!info) return;   // evt.removed: handled by the paired `added` on the target column
            const el = info.element;
            // pre-move server fields — the undo target for the parent's toast
            const from = { user_id: el.user_id, date: el.date, pos: el.pos };
            const list = this.listFor(person.id);
            const pos = this.midpointPos(list, info.newIndex);
            window.axios.put('/planner/slot/' + el.id, {
                user_id: person.id,
                scheduled_date: this.date,
                pos: pos,
            })
                .then(() => {
                    if (from.user_id !== person.id) {
                        this.$emit('moved', { slot_id: el.id, name: el.pretty_name, to_user_id: person.id, from: from });
                    }
                    this.$emit('refetch');
                })
                .catch(err => { this.$emit('error', errMsg(err)); this.$emit('refetch'); });
        },
        midpointPos(list, index) {
            const prev = list[index - 1];
            const next = list[index + 1];
            if (prev && next) return (prev.pos + next.pos) / 2;
            if (prev) return prev.pos + 1;
            if (next) return next.pos / 2;
            return 1;
        },
        capacity(person) {
            return Math.round(((person.hours || {})[this.date] || 0) * 3600);
        },
        planned(person) {
            return this.slotsFor(person).reduce((sum, s) => sum + (s.planned_seconds || 0), 0);
        },
        pct(person) {
            return adjustedPct(this.planned(person), this.capacity(person), this.board.efficiency_percent);
        },

        moveToToday(carry) {
            window.axios.put('/planner/slot/' + carry.slot_id, { scheduled_date: this.board.today })
                .then(() => this.$emit('refetch'))
                .catch(err => this.$emit('error', errMsg(err)));
        },

        // One-tap tick: optimistic, so end-of-day ticking is one tap per
        // row with no board reload between taps. The drawer stays the
        // home of on-behalf-of and everything else.
        quickTick(slot) {
            if (this.ticking[slot.id]) return;
            const next = !slot.checked;
            this.$set(this.ticking, slot.id, true);
            slot.checked = next;
            const req = slot.checklist_id
                ? window.axios.put('/checklist', { type: 'process', id: slot.checklist_id, checked: next })
                : window.axios.post('/planner/slot/' + slot.id + '/tick', { on: next });
            req
                .catch(err => { slot.checked = !next; this.$emit('error', errMsg(err)); })
                .then(() => this.$delete(this.ticking, slot.id));
        },

        reassignPersonDay(p) {
            if (!window.confirm(p.name + ' has ' + fmtSecs(p.planned) + ' planned today but no hours.\n\n'
                + 'Reassign their day? Each process goes to whoever can take it soonest; '
                + 'anything unplaceable returns to the unscheduled tray.')) return;
            window.axios.post('/planner/user/' + p.id + '/reassign-day', { date: this.date })
                .then(() => this.$emit('refetch'))
                .catch(err => this.$emit('error', errMsg(err)));
        },
    },
};
</script>

<style scoped>
.pl-kpis{display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:14px}
.pl-kpi{background:#fcfcfb; border:1px solid #e1e0d9; border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:12px 16px; display:flex; align-items:center; gap:12px}
.pl-kpi-big{font-size:24px; font-weight:800; line-height:1.1}
.pl-kpi-big small{font-size:14px; color:#898781; font-weight:700}
.pl-kpi-lbl{font-size:12.5px; color:#52514e; font-weight:650; line-height:1.3}
.pl-kpi-badge{width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center;
    flex:none; font-size:19px; font-weight:900}
.pl-kpi-good{background:#e3f4e3; color:#0ca30c}
.pl-kpi-warn{background:#fdf3d9; color:#8a6100}
.pl-kpi-crit{background:#f9e4e4; color:#d03b3b}
.pl-kpi-ink{background:#f4f3f0; color:#52514e}

.pl-dayboard{display:grid; grid-template-columns:1fr 320px; gap:14px; align-items:start}
.pl-daycols{display:grid; grid-template-columns:repeat(auto-fit, minmax(230px, 1fr)); gap:12px}
.pl-daycol{background:#fcfcfb; border:1px solid #e1e0d9; border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); overflow:hidden}
.pl-daycol .pl-person{border-bottom:1px solid #e1e0d9; background:#f4f3f0; padding:12px; display:flex; gap:10px; align-items:center}
.pl-day-lim{font-size:11px; color:#52514e; font-weight:700}
.pl-daycap{padding:9px 12px 0}
.pl-daylist{padding:9px 10px 12px; display:flex; flex-direction:column; gap:6px; min-height:56px}
.pl-daychip{cursor:pointer; min-height:40px}
.pl-daychip .pl-i{width:18px; height:18px}
/* Day columns are roomy vertical lists — show the whole process name
   (and job) instead of the grid's one-line ellipsis. */
.pl-daychip .pl-chip-nm{white-space:normal; overflow:visible; text-overflow:clip}
.pl-daychip .pl-chip-cq{white-space:normal; overflow:visible; text-overflow:clip}
/* P4 corner mark. PlannerPage's rule is scoped to that component, so it's
   restated here; .pl-chip{position:relative} comes from the shared
   unscoped block at the bottom of PlannerPage.vue. */
.pl-chip-flag{position:absolute; top:-7px; right:-6px; background:var(--pl-card); border:1px solid var(--pl-line); border-radius:10px;
    font-size:11px; line-height:16px; padding:0 4px; color:var(--pl-ink); pointer-events:none}
.pl-day-dim{opacity:.45}
/* the column that will receive the chip when released */
.pl-daycol-target{outline:2px solid #0ca30c; outline-offset:-2px}
.pl-daycol-target .pl-daylist{background:#e3f4e3}
.pl-tick{width:26px; height:26px; flex:none; border-radius:50%; border:2px solid #c9c7bf; background:#fff;
    cursor:pointer; font-size:14px; font-weight:900; line-height:1; color:#fff; padding:0;
    display:flex; align-items:center; justify-content:center}
.pl-tick:hover{border-color:#0ca30c}
.pl-tick-on{background:#0ca30c; border-color:#0ca30c}
.pl-tick:disabled{opacity:.5; cursor:default}
.pl-day-empty{color:#898781; font-size:12.5px; font-weight:600; text-align:center; padding:14px 0}

.pl-rail{background:#fcfcfb; border:1px solid #e1e0d9; border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:14px}
.pl-rail h2{font-size:15px; font-weight:800; margin:0 0 3px}
.pl-rail-sub{font-size:12.5px; color:#898781; margin-bottom:11px}
.pl-exc{border:1px solid #e1e0d9; border-radius:10px; padding:10px 11px; margin-bottom:8px; background:#f9f9f7}
.pl-exc-tx b{font-size:13.5px; display:block; line-height:1.3}
.pl-exc-tx p{font-size:12.5px; color:#52514e; line-height:1.35; margin:1px 0 0}
.pl-exc-warn{border-left:4px solid #fab219}
.pl-exc-ink{border-left:4px solid #898781}
.pl-exc-ok{border-left:4px solid #0ca30c}
.pl-exc-crit{border-left:4px solid #d03b3b}
.pl-exc-act{margin-top:6px; font-size:12px; font-weight:800; color:#0b0b0b; background:#f4f3f0;
    border:1px solid #e1e0d9; border-radius:7px; padding:4px 9px; cursor:pointer; font-family:inherit}

@media (max-width:1100px){
    .pl-kpis{grid-template-columns:1fr 1fr}
    .pl-dayboard{grid-template-columns:1fr}
}
</style>
